import { Button } from "@/components/ui/button";
import { usePrevious } from "@/hooks/usePrevious";
import { cn } from "@/lib/utils";
import { useMessageStore } from "@/state/stores";
import { useAttachmentStore } from "@/state/stores/attachmentStore";
import { ArrowDownIcon } from "@radix-ui/react-icons";
import { type PropsWithChildren, useEffect, useState } from "react";
import { useStickToBottom } from "use-stick-to-bottom";
import { ChatInput } from "./ChatInput";

export function ChatContainer({ children }: PropsWithChildren) {
  const isDragging = useAttachmentStore((state) => state.isDragging);
  const [hasNewMessages, setHasNewMessages] = useState(false);

  const messagesLength = useMessageStore((state) => state.messages.length);
  const prevMessagesLength = usePrevious(messagesLength);

  const { contentRef, scrollRef, scrollToBottom, isAtBottom } =
    useStickToBottom();

  useEffect(() => {
    const previousCount = prevMessagesLength ?? 0;
    if (messagesLength > previousCount && !isAtBottom) {
      setHasNewMessages(true);
    }
  }, [isAtBottom, messagesLength, prevMessagesLength]);

  const handleScrollToBottom = () => {
    scrollToBottom("smooth");
    setHasNewMessages(false);
  };

  return (
    <main
      ref={scrollRef}
      className="overflow-y-auto flex flex-col h-full relative"
    >
      <div
        ref={contentRef}
        className={cn(
          "w-full max-w-2xl mx-auto px-5 pt-5 flex-1 relative z-0",
          isDragging &&
            "before:content-[''] before:absolute before:inset-0 before:bg-gradient-to-t before:from-background before:to-transparent before:pointer-events-none before:z-10 animate-fadeIn duration-150",
        )}
      >
        {children}

        <div className="sticky bottom-48 w-full pointer-events-none">
          <Button
            onClick={handleScrollToBottom}
            variant="outline"
            size="icon-sm"
            className={cn(
              "absolute left-1/2 -translate-x-1/2 bottom-0 z-50 rounded-full pointer-events-auto p-0 transition-all duration-200 transition-discrete opacity-0 border-focus hidden",
              !isAtBottom && "block opacity-100",
            )}
            aria-label="Scroll to bottom"
          >
            <div className="relative h-full w-full grid place-items-center">
              <ArrowDownIcon />
              {hasNewMessages && (
                <span className="absolute -top-px -right-px h-2 w-2 rounded-full bg-destructive animate-pulse" />
              )}
            </div>
          </Button>
        </div>
      </div>

      <ChatInput onSendMessage={handleScrollToBottom} />
    </main>
  );
}
